import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ApplicationAccessFooter from "../ApplicationAccessFooter/ApplicationAccessFooter";
import { AppDispatch } from "../../store/store";
import { updateCurrentStep } from "../../store/slices/featureGroupWithFeatureSlice";
import { Grid } from "@mui/material";
import * as style from "../ApplicationAccessHeadCard/ApplicationAccessHeadCard.module.scss";
import { getEmailSearchResults } from "../../services/RequestAccess/RequestAccess";
import {
  Dropdown,
  DropdownType,
  Pagination,
} from "cprime-foundational-components";
import GridTable from "cvmgmt-cube-grid-table";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";

type UserOption = {
  id: string;
  label: string;
  user_workday_id?: string;
  region: string;
  department: string;
};

type UserRow = {
  displayName: string;
  mail: string;
  user_workday_id?: string;
  action: string;
};

interface SelectUsersProps {
  selectedUserList: UserRow[];
  initialSelectedUser?: any;
  setIsAddingUser?: (status: boolean) => void;
  onNext?: (selectedUserRows: any[]) => void;
  onCancel?: () => void;
}

const getCount = (rowList: UserRow[] = [], filters: ColumnFilter[] = []) => {
  let rows = [...rowList];

  filters.forEach(({ id, value }) => {
    rows = rows?.filter((row) =>
      row?.[id as keyof UserRow]
        ?.toString()
        ?.toLowerCase()
        ?.includes(String(value).toLowerCase()),
    );
  });

  return rows?.length || 0;
};

export default function SelectUsers({
  selectedUserList,
  initialSelectedUser,
  setIsAddingUser,
  onNext,
  onCancel,
}: SelectUsersProps) {
  const scssClass = style as any;
  const dispatch = useDispatch<AppDispatch>();

  const [userLists, setUserList] = useState<UserOption[]>([]);
  const [userOptionInput, setUserOptionInput] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [gridRefresh, setGridRefresh] = useState<number>(0);

  const [gridData, setGridData] = useState<GridTableDataType>({
    columns: [
      { id: "displayName", name: "Name", size: 200, minSize: 200 },
      { id: "mail", name: "Email", size: 200, minSize: 200 },
      { id: "region", name: "Region", size: 200, minSize: 200 },
      { id: "department", name: "Department Code", size: 200, minSize: 200 },
      { name: "Action", id: "action", type: "ACTION", size: 200, minSize: 200 },
    ],
    rows: [],
    totalCount: 0,
  });

  const [selectedUserRows, setSelectedUserRows] = useState<UserRow[]>(
    selectedUserList?.length > 0 ? selectedUserList : [],
  );

  useEffect(() => {
    // for admin or superadmin
    // if (selectedUserList?.length > 0) {
    //   setSelectedUserRows(selectedUserList);
    setSelectedUserRows(selectedUserList);
    // }
  }, [selectedUserList]);

  const searchUser = async (query: string) => {
    try {
      if (query?.length >= 1) {
        const param: "name" | "email" =
          query.includes("@") || query.includes(".") ? "email" : "name";
        const value = query.trim();

        const response = await getEmailSearchResults(
          encodeURIComponent(value),
          param,
        );

        const results: UserOption[] =
          response?.data?.map((user: any) => ({
            id: user?.mail ?? "",
            label:
              param === "email"
                ? (user?.mail ?? "")
                : (user?.displayName ?? ""),
            user_workday_id: user?.user_workday_id ?? "",
            department: user?.department ?? "",
            region: user?.region ?? "",
          })) ?? [];

        setUserList(results);
      } else {
        setUserList([]);
      }
    } catch {
      setUserList([]);
    }
  };

  const handleSelectedUserDelete = (rowData: UserRow) => {
    const afterDelete = selectedUserRows?.filter(
      (user) => user?.mail !== rowData?.mail,
    );
    setSelectedUserRows(afterDelete);
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [filters]);

  useEffect(() => {
    const applyFiltersAndPagination = () => {
      try {
        let rows = [...(selectedUserRows ?? [])];

        filters?.forEach(({ id, value }) => {
          rows = rows.filter((row) =>
            row?.[id as keyof UserRow]
              ?.toString()
              ?.toLowerCase()
              ?.includes(String(value).toLowerCase()),
          );
        });

        sorting?.forEach(({ id, desc }) => {
          rows.sort((a, b) => {
            const valA =
              a?.[id as keyof UserRow]?.toString()?.toLowerCase() ?? "";
            const valB =
              b?.[id as keyof UserRow]?.toString()?.toLowerCase() ?? "";
            if (valA < valB) return desc ? 1 : -1;
            if (valA > valB) return desc ? -1 : 1;
            return 0;
          });
        });

        const start = pagination.page * pagination.rowsPerPage;
        const end = start + pagination.rowsPerPage;
        const paginatedRows = rows.slice(start, end);

        setGridData((prev) => ({
          ...prev,
          rows: paginatedRows,
          totalCount: rows?.length,
        }));

        setGridRefresh((prev) => prev + 1);
      } catch {
        // Optional: error logging
      }
    };

    applyFiltersAndPagination();
  }, [filters, sorting, pagination, selectedUserRows]);

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  return (
    <div
      className={scssClass?.selectUsersContainer}
      style={{
        gap: "1rem",
        display: "grid",
        ...(selectedUserRows?.length > 0
          ? {
              height: "calc(100vh - 20rem)",
              overflowY: "auto",
            }
          : { height: "fit-content" }),
      }}
    >
      <div>
        <div style={{ margin: "0.5rem 1rem" }}>
          <span
            style={{ fontSize: "14px", lineHeight: "1rem", fontWeight: 600 }}
          >
            Select User(s)
          </span>
        </div>

        <div className={scssClass?.requestAccessContainer}>
          <Grid
            container
            item
            className={scssClass?.body}
            alignItems="center"
            gap="24px"
          >
            <div style={{ display: "grid" }}>
              <span style={{ fontSize: "14px", marginBottom: 1 }}>
                Select User(s) to request for.
              </span>
            </div>

            <Grid
              item
              xs={12}
              className={scssClass?.bodyFirstDiv}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                paddingLeft: "0",
                paddingTop: "0",
              }}
            >
              <Dropdown
                selectedOptions={[]}
                noOptionsText={
                  userOptionInput.trim() === ""
                    ? "Please enter user name"
                    : userLists?.length === 0
                      ? "No options"
                      : ""
                }
                aria-label="select_user"
                className={scssClass?.dropdown}
                width="485px"
                options={userLists}
                placeholder="Search and select user"
                label="Users"
                optionsTextLimit={200}
                type={DropdownType.FilterSingleSelect}
                inputValue={userOptionInput}
                onInputChange={(_, value) => {
                  setUserOptionInput(value);
                  if (debounceTimer) clearTimeout(debounceTimer);
                  const timer = setTimeout(() => searchUser(value), 1000);
                  setDebounceTimer(timer);
                }}
                onChange={(options) => {
                  const selected = options?.[0] as UserOption;
                  if (!selected) return;

                  const alreadyAdded = selectedUserRows?.find(
                    (user) => user?.mail === selected?.id,
                  );

                  if (!alreadyAdded) {
                    const updated = [
                      ...(selectedUserRows ?? []),
                      {
                        displayName: selected?.label ?? "",
                        mail: selected?.id ?? "",
                        user_workday_id: selected?.user_workday_id ?? "",
                        department: selected?.department ?? "",
                        region: selected?.region ?? "",
                        action: "Delete",
                      },
                    ];
                    setSelectedUserRows(updated);
                  }

                  setUserOptionInput("");
                }}
                onClose={() => {}}
                onOpen={() => {
                  if (!userOptionInput) searchUser("");
                }}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      {selectedUserRows?.length > 0 && (
        <div style={{ width: "100%", marginTop: "1rem" }}>
          <GridTable
            style={{
              height: "calc(100vh - 46rem)",
              maxWidth: "calc(100vw - 6rem)",
              minHeight: "10.5rem",
            }}
            aria-label="selected_users"
            data={gridData}
            customPayload={false}
            isPagination={false}
            isCustomizeColumns={false}
            isSorting={true}
            isFiltering={true}
            title="Selected Users"
            refresh={gridRefresh}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            cellActions={[
              {
                event: "Delete",
                callback: (_event, data) => {
                  const rowData = data?.row?.original;
                  handleSelectedUserDelete(rowData);
                },
                icon: true,
              },
            ]}
            showLoader={false}
            loading={false}
          />
          <Pagination
            sx={{ width: "100%" }}
            {...{
              ...pagination,
              count: getCount(selectedUserRows, filters),
              rowsPerPageOptions: [25, 50, 100, 250],
              onPageChange: (_e, number) => {
                setPagination({ ...pagination, page: number });
              },
              onRowsPerPageChange: (evt) => {
                setPagination({
                  page: 0,
                  rowsPerPage: Number(evt?.target?.value),
                });
              },
            }}
          />
        </div>
      )}

      <ApplicationAccessFooter
        isBack={true}
        isSubmit={false}
        isNextDisabled={!(selectedUserRows?.length > 0)}
        onClear={() => initialSelectedUser?.()}
        onBack={() => {
          dispatch(updateCurrentStep(2));
          setIsAddingUser?.(false);
          // Discard any in-progress "on behalf of" selection so the next fetch uses the logged-in user
          onCancel?.();
        }}
        onNext={() => onNext?.(selectedUserRows)}
      />
    </div>
  );
}
